import {
  type EdgeCenterLabel,
  type EdgeLinecapLabel,
  type GraphEdgeSettings,
  type GroupEdgeSettings,
  type PathLookupEdgeSettings,
  type PathLookupEdgeType,
  type ProtocolEdgeType,
  type TopologyEdgeSettings,
  type TopologyGraphPreset,
  defaultEdgeLabelsOptions,
} from '@ipfabric/utils';
import type { EnabledProtocolLabels } from 'components/Graph/helpers/getProtocolLabels';
import { getRandomColorString } from 'components/Graph/helpers/getRandomColorString';
import type { FormSettingsValues } from '../GraphProtocolsSettings';

const getSettingsEntity = (
  preset: TopologyGraphPreset,
  groupId: GroupEdgeSettings['id'] | null,
  protocolName: ProtocolEdgeType | PathLookupEdgeType | undefined,
): GraphEdgeSettings | undefined => {
  if (groupId !== null) {
    const foundGroup = preset.edges.find(
      (group: GraphEdgeSettings): group is GroupEdgeSettings =>
        group.id === groupId,
    );
    if (foundGroup) {
      // protocol in group
      if (protocolName) {
        return foundGroup.children.find(
          (protocol: TopologyEdgeSettings | PathLookupEdgeSettings) =>
            protocol.name === protocolName,
        );
      }
      // group
      return foundGroup;
    }
    return undefined;
  }
  // protocol in Others group
  return preset.edges.find(
    (item: GraphEdgeSettings): item is TopologyEdgeSettings =>
      item.name === protocolName,
  );
};

export const getProtocolSettingsInitialValues = (
  preset: TopologyGraphPreset,
  groupId: GroupEdgeSettings['id'] | null,
  protocolName: ProtocolEdgeType | PathLookupEdgeType | undefined,
  labels: EnabledProtocolLabels,
): FormSettingsValues => {
  const settingsEntity = getSettingsEntity(preset, groupId, protocolName);

  if (!settingsEntity) {
    return {
      color: '',
      pattern: 'dashed',
      labelCenter: [],
      labelLinecap: [],
      wrapCenterLabel: defaultEdgeLabelsOptions.wrapCenterLabel,
      wrapLinecapLabel: defaultEdgeLabelsOptions.wrapCenterLabel,
      lineThicknessThin: 1,
      lineThicknessMedium: 1,
      lineThicknessThick: 1,
    };
  }

  return {
    color: settingsEntity.style.color,
    pattern: settingsEntity.style.pattern,
    lineThicknessThin: settingsEntity.style.thicknessThresholds[0],
    lineThicknessMedium: settingsEntity.style.thicknessThresholds[1],
    lineThicknessThick: settingsEntity.style.thicknessThresholds[2],
    labelCenter:
      (Object.keys(labels.center).filter(
        (key) => labels.center[key as EdgeCenterLabel],
      ) as EdgeCenterLabel[]) ?? [],
    labelLinecap:
      (Object.keys(labels.linecap).filter(
        (key) => labels.linecap[key as EdgeLinecapLabel],
      ) as EdgeLinecapLabel[]) ?? [],
    ...(settingsEntity.type === 'group' && {
      label: settingsEntity.label,
      name: settingsEntity.name,
    }),
    wrapCenterLabel:
      settingsEntity.labelsOptions?.wrapCenterLabel ??
      defaultEdgeLabelsOptions.wrapCenterLabel,
    wrapLinecapLabel:
      settingsEntity.labelsOptions?.wrapLinecapLabel ??
      defaultEdgeLabelsOptions.wrapLinecapLabel,
  };
};

export const getCreateGroupInitialValues = (): FormSettingsValues => ({
  color: getRandomColorString(),
  pattern: 'solid',
  labelCenter: [],
  labelLinecap: [],
  wrapCenterLabel: defaultEdgeLabelsOptions.wrapCenterLabel,
  wrapLinecapLabel: defaultEdgeLabelsOptions.wrapLinecapLabel,
  lineThicknessThin: 2,
  lineThicknessMedium: 4,
  lineThicknessThick: 8,
  label: '',
  name: '',
});
